import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Check, Sparkles, Telescope } from "lucide-react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const STATIC_PERKS = {
  free: {
    headline: "Pour découvrir",
    perks: ["1 image traitée / mois", "Les 5 modes de traitement", "Médiathèque privée", "Exports JPG / PNG / TIFF"],
    cta: "Démarrer gratuitement",
  },
  monthly: {
    headline: "Sans engagement",
    perks: ["Traitements illimités", "Mode Lot (jusqu'à 20 images)", "FITS / XISF", "Lien de partage public", "Annulation à tout moment"],
    cta: "Choisir Mensuel",
    accent: true,
  },
  annual: {
    headline: "Économisez 35 %",
    perks: ["Tout le Mensuel inclus", "Économie de 35 % vs mensuel", "Tarif équivalent mensuel le plus bas", "Priorité support"],
    cta: "Choisir Annuel",
    badge: "Économisez 35 %",
  },
};


function fmtPrice(p) {
  return p === 0 ? "0" : p.toString().replace(".", ",");
}


export default function Pricing() {
  const { user } = useAuth();
  const [plans, setPlans] = useState(null);
  const [billing, setBilling] = useState(null);
  const [busy, setBusy] = useState(null);
  const [search] = useSearchParams();
  const nav = useNavigate();

  useEffect(() => {
    api.get("/billing/plans").then((r) => setPlans(r.data)).catch(() => setPlans([]));
    if (search.get("cancelled")) toast.info("Paiement annulé. Vous pouvez réessayer quand vous voulez.");
    if (search.get("quota")) toast.warning("Quota gratuit atteint. Choisissez un forfait pour continuer.");
    if (user) api.get("/billing/me").then((r) => setBilling(r.data)).catch(() => {});
  }, [user, search]);

  const onChoose = async (plan) => {
    if (!user) { nav("/login"); return; }
    if (plan === "free") { nav("/"); return; }
    setBusy(plan);
    try {
      const { data } = await api.post("/billing/checkout", { plan, origin: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec du paiement");
      setBusy(null);
    }
  };

  const cards = (plans || []).map((p) => ({
    ...p,
    ...STATIC_PERKS[p.key],
    monthly_equiv: p.key === "annual" ? +(p.price / 12).toFixed(2) : null,
  }));

  return (
    <div className="min-h-screen relative">
      <div className="starfield" aria-hidden />
      <header className="sticky top-0 z-40 glass border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to={user ? "/" : "/login"} className="flex items-center gap-3" data-testid="pricing-home-link">
            <div className="w-9 h-9 rounded-md border border-white/10 grid place-items-center bg-gradient-to-br from-violet-500/30 to-blue-500/10">
              <Telescope className="w-4 h-4 text-violet-200" strokeWidth={1.5} />
            </div>
            <div className="font-medium tracking-tight">AstroLift</div>
          </Link>
          <div className="text-xs text-muted-foreground hidden sm:block">Paiement sécurisé · Stripe</div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-16">
        <div className="text-center max-w-2xl mx-auto">
          <div className="label-eyebrow">Tarifs</div>
          <h1 className="mt-3 text-4xl sm:text-5xl font-light tracking-tighter">Sublimer vos astrophotos, sans limite.</h1>
          <p className="mt-4 text-muted-foreground">Annulable à tout moment. TVA incluse.</p>
        </div>

        {billing && !billing.is_admin && (
          <div className="mt-8 text-center text-sm text-muted-foreground" data-testid="current-plan">
            Plan actuel : <span className="text-white font-medium">{billing.plan_label}</span>
            {billing.plan !== "free" && billing.plan_expires_at && (
              <> · jusqu'au <span className="font-mono">{new Date(billing.plan_expires_at).toLocaleDateString("fr-FR")}</span></>
            )}
            <> · {billing.used_this_month} / {billing.monthly_quota} image(s) ce mois-ci</>
          </div>
        )}

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
          {cards.map((p, i) => {
            const isCurrent = billing?.plan === p.key;
            return (
              <motion.div
                key={p.key}
                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.08 }}
                className={`relative rounded-md border bg-card p-6 flex flex-col ${p.accent ? "border-primary glow-violet" : "border-white/10"}`}
                data-testid={`plan-card-${p.key}`}
              >
                {p.badge && (
                  <div className="absolute -top-3 right-4 px-2.5 py-0.5 rounded-full bg-gradient-to-r from-violet-500 to-blue-500 text-[10px] font-mono tracking-wider uppercase">
                    {p.badge}
                  </div>
                )}
                <div className="label-eyebrow">{p.headline}</div>
                <div className="mt-2 text-2xl font-light tracking-tight">{p.label}</div>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className="text-5xl font-light tracking-tighter">{fmtPrice(p.price)}</span>
                  <span className="text-xl text-muted-foreground">€</span>
                  <span className="text-sm text-muted-foreground ml-1">/ {p.interval}</span>
                </div>
                {p.monthly_equiv && (
                  <div className="mt-1 text-xs text-violet-300 font-mono">≈ {p.monthly_equiv.toString().replace(".", ",")} €/mois</div>
                )}
                <ul className="mt-6 space-y-2.5 flex-1">
                  {p.perks.map((perk) => (
                    <li key={perk} className="flex items-start gap-2 text-sm">
                      <Check className={`w-4 h-4 mt-0.5 flex-none ${p.accent ? "text-violet-300" : "text-muted-foreground"}`} />
                      <span>{perk}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => onChoose(p.key)}
                  disabled={busy === p.key || isCurrent || billing?.is_admin}
                  data-testid={`plan-cta-${p.key}`}
                  className={`mt-6 w-full inline-flex items-center justify-center gap-2 rounded-md py-2.5 text-sm font-medium transition-all disabled:opacity-60 disabled:cursor-not-allowed ${p.accent ? "bg-primary text-primary-foreground hover:bg-primary/90 glow-violet" : "border border-white/10 hover:bg-white/5"}`}
                >
                  {busy === p.key ? "Redirection…" : billing?.is_admin ? "Admin illimité" : isCurrent ? "Plan actuel" : p.cta}
                  {!busy && !isCurrent && !billing?.is_admin && <Sparkles className="w-3.5 h-3.5" />}
                </button>
              </motion.div>
            );
          })}
        </div>

        <p className="mt-12 text-center text-xs text-muted-foreground/70 max-w-xl mx-auto">
          Paiement traité par Stripe. Mode test pour la pré-version : utilisez la carte <span className="font-mono text-white">4242 4242 4242 4242</span>, n'importe quelle date future, n'importe quel CVC.
        </p>
      </main>
    </div>
  );
}
